import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { label: "", name: "", phone: "", region: "", country: "", city: "", address: "", landmark: "", is_default: false };

export default function AddressDialog({ open, onOpenChange, initial, onSaved }) {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const inputCls =
    "mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, lat: null, lng: null };
    try {
      if (initial?.id) {
        await api(`/me/addresses/${initial.id}`, { method: "PUT", auth: true, body });
        toast.success("Address updated");
      } else {
        await api("/me/addresses", { method: "POST", auth: true, body });
        toast.success("Address saved");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error("Could not save address", { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="address-dialog" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial ? "Edit address" : t("add_address")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Label</label>
              <input data-testid="address-label-input" value={form.label} onChange={set("label")} placeholder="Home, Office..." className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Recipient name</label>
              <input data-testid="address-name-input" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Phone</label>
              <input data-testid="address-phone-input" required minLength={7} value={form.phone} onChange={set("phone")} placeholder="+255..." className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Country</label>
              <input data-testid="address-country-input" value={form.country} onChange={set("country")} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">City</label>
              <input data-testid="address-city-input" required minLength={2} value={form.city} onChange={set("city")} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Region</label>
              <input data-testid="address-region-input" value={form.region} onChange={set("region")} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Street address</label>
            <input data-testid="address-street-input" required minLength={3} value={form.address} onChange={set("address")} className={inputCls} />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Landmark (optional)</label>
            <input data-testid="address-landmark-input" value={form.landmark} onChange={set("landmark")} className={inputCls} />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input data-testid="address-default-checkbox" type="checkbox" checked={form.is_default} onChange={set("is_default")} className="h-4 w-4 accent-teal-600" />
            Set as my default address
          </label>
          <button
            data-testid="address-save-button"
            type="submit"
            disabled={saving}
            className="w-full rounded-lg bg-teal-600 py-2.5 text-sm font-bold text-white hover:bg-teal-700 transition active:scale-95 disabled:opacity-60"
          >
            {saving ? "Saving..." : initial ? "Save changes" : t("add_address")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
